import React, { useCallback, useState } from 'react';

import { gql } from '@apollo/client';
import { useTranslation } from '@modernloop/i18n';
import { Dialog } from '@modernloop/shared/components';

import { useTaskSetDebriefRequiredMutation } from 'src/generated/mloop-graphql';

import TrashIllustration from 'src/assets/images/illustrations/trash_64.png';

import { useActivityFeedRefetch } from 'src/hooks/useActivityFeedRefetch';

type RemoveDebriefRequirementProps = {
  taskId: string;
  open: boolean;
  onClose: () => void;
  onRemove?: () => void;
};

const RemoveDebriefRequirementConfirmation = ({ taskId, open, onClose, onRemove }: RemoveDebriefRequirementProps) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [taskSetDebriefRequiredMutation] = useTaskSetDebriefRequiredMutation();
  const activityFeedRefetch = useActivityFeedRefetch();

  const handleRemoveDebriefRequirement = useCallback(() => {
    setLoading(true);
    // eslint-disable-next-line promise/catch-or-return
    taskSetDebriefRequiredMutation({
      variables: {
        input: {
          taskId,
          isDebriefRequired: false,
        },
      },
      optimisticResponse: {
        taskSetDebriefRequired: {
          id: taskId,
          isDebriefRequired: false,
        },
      },
    })
      .then(() => activityFeedRefetch())
      .finally(() => {
        setLoading(false);
        onClose();
        onRemove?.();
      });
  }, [activityFeedRefetch, onClose, onRemove, taskId, taskSetDebriefRequiredMutation]);

  return (
    <Dialog
      dataTestId="delete-tag-modal"
      open={open}
      onClose={onClose}
      title={t('hiring-dashboard-tasks:tasks.removeRequirement.title')}
      subTitle={t('hiring-dashboard-tasks:tasks.removeRequirement.subtitle')}
      decoration={
        <img
          style={{ width: '64px', height: '64px' }}
          src={TrashIllustration}
          alt={t('hiring-dashboard-tasks:tasks.removeRequirement.deleteAlt')}
        />
      }
      cancelOptions={{
        onClick: onClose,
      }}
      submitOptions={{
        label: t('hiring-dashboard-tasks:tasks.removeRequirement.confirmLabel'),
        onClick: handleRemoveDebriefRequirement,
        isDangerous: true,
        isDisabled: loading,
        isLoading: loading,
      }}
    >
      {t('hiring-dashboard-tasks:tasks.removeRequirement.body')}
    </Dialog>
  );
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const TaskSetDebriefRequiredMutation = gql`
  mutation TaskSetDebriefRequired($input: TaskSetDebriefRequiredInput!) {
    taskSetDebriefRequired(input: $input) {
      id
      isDebriefRequired
    }
  }
`;

export default RemoveDebriefRequirementConfirmation;
